/**
 * Welcome-screen suggestion generator (PLT — chat suggestion contextualisation
 * follow-up to PLT-4625).
 *
 * Purpose: take whatever the chat panel currently knows about the user's
 * context — entity type (company / group / portfolio), entity name, the lens
 * they were on (Net impact, Double materiality, Financial effects, …), and
 * any detail label like a DMA matter title — and produce 3–4 suggestion
 * chips that feel specific to *this* view rather than the same four
 * "Tesla / Apple / Nordea / Screen tech" pills everywhere.
 *
 * Design notes:
 *
 * - **Pure function, no React.** Lives next to `upright-chat-welcome.tsx`
 *   so the welcome screen can call it on render. Also unit-testable
 *   without mounting a tree.
 *
 * - **Determinism with a daily salt.** A seeded RNG (`mulberry32`) seeds
 *   from `hash(entityType + entityName + lensLabel + detailLabel +
 *   dayBucket)`. That means: opening the same panel twice in a row
 *   shows the same chips (no flicker on re-open or refresh), but the
 *   wildcard slot rotates roughly daily, and different (company, lens,
 *   matter) combinations naturally land on different mixes. Avoids the
 *   "same four strings forever" failure mode without going full random
 *   per-render.
 *
 * - **Entity-type-aware framing.** A fund (`group`) and a portfolio
 *   are not a single company — the chips reframe accordingly ("Top
 *   contributing companies", "Geographic exposure", "Holdings driving
 *   {portfolio}'s impact") rather than asking about "products" the way
 *   the company chips do. A `supplier` is a company, but the reader is a
 *   buyer rather than an analyst, so it gets its own set too (PLT-6269).
 *
 * - **Lens-aware anchor.** When we know which lens the user came from,
 *   slot 1 leads with a question that lens can actually answer well —
 *   "Which sustainability matters are most material…" for DMA,
 *   "What is driving the positive/negative impact…" for Net impact,
 *   "What is the largest financial risk…" for FE, etc. The mapping
 *   defers to `current-page-chat-path-context.ts`'s lens labels
 *   verbatim, so adding a new lens there feeds through here without
 *   another mapping table to maintain.
 *
 * - **Detail-aware drill-in.** If the path context carries a
 *   `detailLabel` (e.g. the matter title on a DMA single-matter page),
 *   slot 2 mentions it directly: "Walk me through {matter} for {company}"
 *   instead of always falling back to climate or workforce as guesses.
 *
 * - **Cap at 4.** More chips becomes wallpaper. Standalone /chat keeps
 *   its existing 4-pill layout (PLT-4605's "long pill on its own row"
 *   treatment lives in the welcome component itself, not here).
 *
 * - **No backend.** Generator only uses what the chat plumbing already
 *   has in module-level state. No new fetches, no new MCP tools.
 */

// ---------------------------------------------------------------------------
// Public types.
// ---------------------------------------------------------------------------

/** A single suggestion chip. */
export interface WelcomeSuggestion {
  /** Short label rendered inside the chip. Character-capped only on the
   *  narrow side-panel surface (see `layout` on `WelcomeSuggestionsInput`);
   *  on the full-page `/chat` landing it is left whole for CSS to size. */
  text: string;
  /** Full prompt sent to the chat when the user clicks the chip. */
  prompt: string;
}

/**
 * Input snapshot the generator works from. All fields are optional so the
 * generator can be called from the welcome screen with whatever context
 * happens to be available — falls through to a generic "explore" set
 * when everything is `null`.
 */
export interface WelcomeSuggestionsInput {
  /** Entity name, e.g. `"Neste"`. Extracted by the caller from the page-context
   *  chip label (`"Neste — Net impact"` → `"Neste"`). */
  entityName: string | null;
  /**
   * Entity type as published by the outer entity view — `'company'`,
   * `'group'`, `'portfolio'`. Drives whether we use single-company copy
   * or fund / portfolio framing.
   *
   * **`'supplier'` is a framing, not a fifth entity type** (PLT-6269). A
   * supplier *is* a company, and the chat binds it as one: the seed's
   * top-level `entityType` stays `'company'`, so the hidden entity context
   * and `upright-chat-adapter.ts`'s company-scoped gate are untouched. What
   * differs is only who is asking and why — a buyer looking at one supplier's
   * risk assessment, not an analyst looking at a company in its own right — so
   * the page-context producer (`supplier-results-panel.tsx`) asks for the
   * framing on its `pageContext` and the welcome screen passes it here.
   */
  entityType: 'company' | 'group' | 'portfolio' | 'supplier' | null;
  /** Lens label, e.g. `'Net impact'`, `'Double materiality'`,
   *  `'Financial effects'`. Sourced from `useStickyPageChatPathContext()`. */
  lensLabel: string | null;
  /** Further qualifier — e.g. matter title on a DMA single-matter page.
   *  May be a long string; character-capped in chip labels on the side panel
   *  but kept whole in the prompt sent to chat. */
  detailLabel: string | null;
  /**
   * Which chat surface the chips will render on. Drives whether chip labels
   * get the character cap (`truncateLabel`):
   *   - `'side-panel'` — the narrow (~360–600px) side panel. Long labels are
   *     capped so they stay compact; the panel's own CSS
   *     (`.chat-side-panel-body …`) then wraps any remainder to two lines.
   *   - `'full-page'` — the wide standalone `/chat` landing, where chips are
   *     styled `max-width: none; white-space: nowrap`, so CSS can expand any
   *     single-line label. We deliberately skip the cap here: capping cut
   *     labels ("Why is climate material for Shell?", "Most material matters
   *     for European utilities") that had ample room to show in full (PLT-5801).
   * Defaults to `'full-page'`.
   */
  layout?: 'full-page' | 'side-panel';
  /**
   * Optional "now" for the daily-rotation salt. Defaults to `Date.now()`.
   * Tests inject a fixed value so output stays deterministic across runs.
   */
  now?: number;
}

// ---------------------------------------------------------------------------
// PRNG + seed.
// ---------------------------------------------------------------------------

/**
 * Stable 32-bit hash of a string (FNV-1a). Good enough as a PRNG seed
 * for picking from small pools — we do NOT need cryptographic strength,
 * just stability across page loads and uniform distribution across
 * different inputs.
 */
function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    // FNV prime; the `>>> 0` keeps us in unsigned-32 land so subsequent
    // bit operations don't run into JS's signed-int weirdness.
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

/**
 * `mulberry32` PRNG. Tiny, deterministic, and good enough for "pick one
 * of N options" — we are not doing statistics here, we just want a
 * stable pseudo-random pick that varies across seeds.
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Pick one element from `pool` using `rng`. Caller guarantees `pool.length > 0`. */
function pick<T>(rng: () => number, pool: readonly T[]): T {
  const idx = Math.floor(rng() * pool.length);
  return pool[idx];
}

/**
 * Pick `n` distinct elements from `pool` using `rng`. If `pool.length <=
 * n`, returns the pool in its original order (no shuffle) — predictable
 * fallback for small pools.
 */
function pickDistinct<T>(rng: () => number, pool: readonly T[], n: number): T[] {
  if (pool.length <= n) return pool.slice();
  const indices = new Set<number>();
  while (indices.size < n) {
    indices.add(Math.floor(rng() * pool.length));
  }
  return [...indices].map(i => pool[i]);
}

/** ~daily bucket. Salt for the seed so chips rotate gently over time. */
function dayBucket(now: number): number {
  return Math.floor(now / 86400000);
}

// ---------------------------------------------------------------------------
// Templating.
// ---------------------------------------------------------------------------

/** Max chip-label length before we cap. Picked to keep chips on a single row
 *  at the narrow side-panel width. Applied ONLY on the side-panel surface (see
 *  `applyLayoutCap`); the full string is always passed through to the prompt
 *  body regardless. */
const MAX_CHIP_LABEL_CHARS = 32;

function truncateLabel(s: string): string {
  if (s.length <= MAX_CHIP_LABEL_CHARS) return s;
  // Prefer a clean break on a comma or em-dash first; fall back to a
  // hard ellipsis. Keeps things like "Climate change mitigation,
  // adaptation and energy" from breaking mid-word.
  const breakChars = [' — ', ' – ', ', '];
  for (const ch of breakChars) {
    const idx = s.indexOf(ch);
    if (idx > 0 && idx <= MAX_CHIP_LABEL_CHARS) return s.slice(0, idx);
  }
  return s.slice(0, MAX_CHIP_LABEL_CHARS - 1).trimEnd() + '…';
}

/**
 * Format a chip from a label + prompt. The label is kept whole here; the
 * side-panel character cap is applied as a final, layout-aware pass in
 * `buildWelcomeSuggestions` (`applyLayoutCap`) so the full-page surface can
 * keep long-but-fitting labels intact.
 */
function chip(text: string, prompt: string): WelcomeSuggestion {
  return { text, prompt };
}

/**
 * Apply the chip-label character cap for the given layout. The cap only makes
 * sense where horizontal space is tight, so it runs on the side panel and is a
 * no-op on the full-page `/chat` landing, where CSS (`max-width: none;
 * white-space: nowrap`) sizes each chip to fit its single line (PLT-5801).
 */
function applyLayoutCap(chips: WelcomeSuggestion[], layout: 'full-page' | 'side-panel'): WelcomeSuggestion[] {
  if (layout !== 'side-panel') return chips;
  return chips.map(c => ({ ...c, text: truncateLabel(c.text) }));
}

// ---------------------------------------------------------------------------
// Pools.
//
// Each helper returns a pool of `(text, prompt)` pairs for a slot.
// Keeping them as plain functions of the resolved `entityName` /
// `lensLabel` rather than templating-at-pick-time so the truncation
// rule above operates on the final label string.
//
// Pools are *intentionally short* — 2–4 phrasings per slot. Long
// pools encourage adding marginal variants that all read the same.
// ---------------------------------------------------------------------------

/** Drivers / "why" anchor pool, by lens. Falls back to a lens-agnostic pool. */
function driversPool(entityName: string, lensLabel: string | null): readonly WelcomeSuggestion[] {
  switch (lensLabel) {
    case 'Net impact':
      return [
        chip(`Drivers of ${entityName}'s impact`, `What is driving the net impact of ${entityName}?`),
        chip(`Negative impact drivers`, `What is driving the negative impact of ${entityName}?`),
        chip(`Positive impact drivers`, `What is driving the positive impact of ${entityName}?`),
      ];
    case 'Double materiality':
      return [
        chip(`Most material matters`, `Which sustainability matters are most material for ${entityName}?`),
        chip(`Why these matters?`, `Why are the most material matters for ${entityName} ranked the way they are?`),
        chip(
          `Impact vs. financial materiality`,
          `Where do impact and financial materiality diverge for ${entityName}?`,
        ),
      ];
    case 'Financial effects':
      return [
        chip(
          `Largest financial risk`,
          `What is the largest financial risk in ${entityName}'s sustainability assessment?`,
        ),
        chip(`Largest opportunity`, `What is the largest financial opportunity for ${entityName}?`),
        chip(`Profit impact summary`, `Summarize the profit-impact estimate for ${entityName}.`),
      ];
    case 'UN SDG alignment':
      return [
        chip(`Top SDG alignment`, `Which UN SDGs is ${entityName} most aligned with?`),
        chip(`Strongest SDG contributions`, `Which products drive ${entityName}'s strongest SDG contributions?`),
        chip(`SDG gaps`, `Which UN SDGs is ${entityName} weakest on?`),
      ];
    case 'EU taxonomy':
      return [
        chip(`Taxonomy alignment`, `What share of ${entityName}'s activity is EU Taxonomy aligned?`),
        chip(`Eligible vs. aligned`, `Where does ${entityName} have taxonomy-eligible but not aligned activity?`),
      ];
    case 'EU SFDR PAIs':
      return [
        chip(`Biggest PAI exposures`, `Which PAI indicators look worst for ${entityName}?`),
        chip(`PAI vs. peers`, `How do ${entityName}'s PAI indicators compare to peers?`),
      ];
    case 'Company insights':
    case 'Company targets':
    case null:
    default:
      return [
        chip(`Sustainability summary`, `Summarize the sustainability profile of ${entityName}.`),
        chip(`Key strengths`, `What are the sustainability strengths of ${entityName}?`),
        chip(`Key risks`, `What are the main sustainability risks for ${entityName}?`),
      ];
  }
}

/** Methodology / explanation slot, by lens. */
function methodologyPool(entityName: string, lensLabel: string | null): readonly WelcomeSuggestion[] {
  switch (lensLabel) {
    case 'Net impact':
      return [
        chip(
          `How is net impact measured?`,
          `How does Upright measure net impact, and how should I read ${entityName}'s figures?`,
        ),
        chip(`What does c/$ mean?`, `What does the c/$ figure represent in ${entityName}'s net impact?`),
      ];
    case 'Double materiality':
      return [
        chip(
          `How is materiality scored?`,
          `How does Upright score double materiality, and how does that apply to ${entityName}?`,
        ),
        chip(
          `Impact vs. financial axis`,
          `What's the difference between impact and financial materiality on ${entityName}'s DMA?`,
        ),
      ];
    case 'Financial effects':
      return [
        chip(
          `How is profit impact estimated?`,
          `How does Upright estimate the financial effects shown for ${entityName}?`,
        ),
      ];
    case 'UN SDG alignment':
      return [
        chip(
          `How is SDG alignment scored?`,
          `How does Upright compute SDG alignment, and how is ${entityName}'s scored?`,
        ),
      ];
    case 'EU taxonomy':
      return [
        chip(
          `Eligibility vs. alignment`,
          `What's the difference between EU Taxonomy eligibility and alignment for ${entityName}?`,
        ),
      ];
    case 'EU SFDR PAIs':
      return [
        chip(
          `How are PAIs computed?`,
          `How does Upright compute SFDR PAI indicators, and what do ${entityName}'s figures mean?`,
        ),
      ];
    case null:
    default:
      return [chip(`How is this measured?`, `How does Upright assess ${entityName}, and what data is behind it?`)];
  }
}

/**
 * Wildcard pool — peers, levers, value chain (Juho's request), and
 * cross-lens nudges. Always include the value-chain prompt; the rest
 * are picked at random per the seeded RNG.
 */
function wildcardPool(entityName: string, lensLabel: string | null): readonly WelcomeSuggestion[] {
  // Always-eligible wildcards. The value-chain entry is the explicit
  // "give the chat MCP a route into get_product_value_chain" hook
  // (Juho's note on the panel chips).
  const base: WelcomeSuggestion[] = [
    chip(
      `Where in the value chain?`,
      `Where in ${entityName}'s value chain do the biggest sustainability impacts arise?`,
    ),
    chip(`Industry comparison`, `How does ${entityName} compare to its industry peers on sustainability?`),
    chip(`What could move the needle?`, `What would most improve ${entityName}'s sustainability profile?`),
    chip(`Comparable companies`, `Show me companies similar to ${entityName} on sustainability.`),
  ];
  // Lens-flavoured wildcards layered on top of the base pool — gives the
  // wildcard slot a richer variety on lens-bound opens without bloating
  // the pool everywhere.
  switch (lensLabel) {
    case 'Net impact':
      base.push(
        chip(`Net impact vs. SDG alignment`, `How does ${entityName}'s net impact line up with its SDG alignment?`),
        chip(`Top contributing products`, `Which products contribute most to ${entityName}'s net impact, and why?`),
      );
      break;
    case 'Double materiality':
      base.push(
        chip(
          `DMA vs. financial effects`,
          `How do ${entityName}'s most material matters map to estimated financial effects?`,
        ),
        chip(`Stakeholders affected`, `Which stakeholders are most affected by ${entityName}'s most material matters?`),
      );
      break;
    case 'Financial effects':
      base.push(
        chip(
          `Risk vs. opportunity balance`,
          `Is ${entityName} net long or short on sustainability — more risk or more opportunity?`,
        ),
      );
      break;
    case 'UN SDG alignment':
      base.push(
        chip(`SDGs vs. net impact`, `How does ${entityName}'s SDG alignment line up with its net impact profile?`),
      );
      break;
    case 'EU taxonomy':
      base.push(
        chip(`Green CapEx outlook`, `What does ${entityName}'s EU Taxonomy alignment suggest about its green CapEx?`),
      );
      break;
    case 'EU SFDR PAIs':
      base.push(chip(`Worst PAI exposure`, `What is ${entityName}'s single worst PAI exposure, and what drives it?`));
      break;
    case null:
      // No lens bound — the lens-agnostic `base` pool is the full set.
      break;
  }
  return base;
}

/**
 * Detail-aware chip when a `detailLabel` is present (e.g. matter title
 * on DMA single-matter page). Keeps the long detail in the prompt; the
 * chip label is capped later on the side panel via `applyLayoutCap`.
 */
function detailChip(entityName: string, lensLabel: string | null, detailLabel: string): WelcomeSuggestion {
  if (lensLabel === 'Double materiality') {
    return chip(`Walk through ${detailLabel}`, `Walk me through the ${detailLabel} materiality of ${entityName}.`);
  }
  if (lensLabel === 'Net impact') {
    return chip(`Drill into ${detailLabel}`, `What is driving ${entityName}'s ${detailLabel} impact?`);
  }
  if (lensLabel === 'Financial effects') {
    return chip(`Drill into ${detailLabel}`, `What is the financial effect of ${detailLabel} on ${entityName}?`);
  }
  // Lens unknown — still useful to acknowledge the detail label.
  return chip(`More on ${detailLabel}`, `Tell me more about ${detailLabel} in ${entityName}'s sustainability profile.`);
}

// ---------------------------------------------------------------------------
// Entity-type-specific generators.
// ---------------------------------------------------------------------------

/** Single-company chip set — uses the lens-aware pools above. */
function buildCompanySuggestions(
  rng: () => number,
  entityName: string,
  lensLabel: string | null,
  detailLabel: string | null,
): WelcomeSuggestion[] {
  const chips: WelcomeSuggestion[] = [];

  // Slot 1 — drivers / why. Pick one from the lens-aware pool.
  chips.push(pick(rng, driversPool(entityName, lensLabel)));

  // Slot 2 — detail-aware drill-in OR a "value chain" / "next-level"
  // chip when we have no detail. The value-chain chip is intentionally
  // *not* in the wildcard pool when used here — we want to guarantee
  // Juho's note is covered on most lens-bound opens (and the wildcard
  // pool can land on it too via its base, which is fine — that's a
  // second wildcard rolling onto the same theme, but slot 4's RNG path
  // makes that unlikely).
  if (detailLabel !== null && detailLabel.length > 0) {
    chips.push(detailChip(entityName, lensLabel, detailLabel));
  } else {
    chips.push(
      chip(
        `Where in the value chain?`,
        `Where in ${entityName}'s value chain do the biggest sustainability impacts arise?`,
      ),
    );
  }

  // Slot 3 — methodology / explanation. Pick from the lens pool.
  chips.push(pick(rng, methodologyPool(entityName, lensLabel)));

  // Slot 4 — wildcard. Pick from the rotating pool; filter out anything
  // that duplicates one of the earlier slots' prompts so the user never
  // sees two chips firing the same prompt.
  const wildcard = wildcardPool(entityName, lensLabel).filter(c => !chips.some(p => p.prompt === c.prompt));
  if (wildcard.length > 0) {
    chips.push(pick(rng, wildcard));
  }

  return chips;
}

/** Fund / peer-group chips — reframed away from "products" toward
 *  "constituents" + portfolio-style framing. */
function buildGroupSuggestions(rng: () => number, entityName: string): WelcomeSuggestion[] {
  const driversAndCoverage: WelcomeSuggestion[] = [
    chip(`Top contributors`, `Which companies contribute most to ${entityName}'s sustainability profile?`),
    chip(`Sector mix`, `What is the sector mix of ${entityName}, and how does it shape its sustainability profile?`),
    chip(`Geographic exposure`, `Where geographically is ${entityName} most exposed on sustainability?`),
  ];
  const risksAndOpps: WelcomeSuggestion[] = [
    chip(
      `Worst sustainability risks`,
      `What are the largest sustainability risks across ${entityName}'s constituents?`,
    ),
    chip(`Strongest sustainability bets`, `Which constituents of ${entityName} look strongest on sustainability?`),
  ];
  const methodology: WelcomeSuggestion[] = [
    chip(
      `How is the group scored?`,
      `How does Upright aggregate sustainability scores across the constituents of ${entityName}?`,
    ),
  ];
  const wildcards: WelcomeSuggestion[] = [
    chip(`Compare to a benchmark`, `How does ${entityName} compare to a relevant market benchmark on sustainability?`),
    chip(`Screen by SDG`, `Which constituents of ${entityName} are most aligned with the UN SDGs?`),
    chip(`Climate exposure`, `How exposed is ${entityName} to climate-related risks across its constituents?`),
  ];

  return [pick(rng, driversAndCoverage), pick(rng, risksAndOpps), pick(rng, methodology), pick(rng, wildcards)];
}

/** Portfolio chips — customer's own portfolio, investor framing. */
function buildPortfolioSuggestions(rng: () => number, entityName: string): WelcomeSuggestion[] {
  const drivers: WelcomeSuggestion[] = [
    chip(`Top holdings driving impact`, `Which holdings drive ${entityName}'s overall sustainability impact?`),
    chip(
      `Sector concentration`,
      `Where is ${entityName} concentrated by sector, and what does that mean for sustainability?`,
    ),
  ];
  const risks: WelcomeSuggestion[] = [
    chip(`Top sustainability risks`, `What are the largest sustainability risks across ${entityName}?`),
    chip(`Worst SFDR PAI exposures`, `Which SFDR PAI indicators look worst for ${entityName}?`),
  ];
  const methodology: WelcomeSuggestion[] = [
    chip(
      `How is the portfolio scored?`,
      `How does Upright aggregate sustainability scores across ${entityName}'s holdings?`,
    ),
  ];
  const wildcards: WelcomeSuggestion[] = [
    chip(`Screen by SDG`, `Which holdings in ${entityName} are most aligned with the UN SDGs?`),
    chip(`EU Taxonomy alignment`, `What share of ${entityName} is EU Taxonomy aligned?`),
    chip(`Rebalance ideas`, `Which holdings most drag down ${entityName}'s sustainability profile?`),
    chip(`Climate exposure`, `How climate-exposed is ${entityName} across its holdings?`),
  ];

  return [pick(rng, drivers), pick(rng, risks), pick(rng, methodology), pick(rng, wildcards)];
}

/**
 * Supplier chips — a buyer's four questions about one supplier
 * (PLT-6269, Roni in the ticket thread on 2026-09-15).
 *
 * **Three fixed slots and one rotating**, where every other builder in this file
 * rotates all four. The three are not interchangeable, and one of them is the
 * reason the entry point exists at all: the supplier results panel deliberately
 * carries no executive summary and no take-aways (decision D25 — the platform
 * does not store them), so "Continue in chat" *is* the route to a summary. A
 * daily salt that sometimes withheld it would be rotating away the answer the
 * reader came for. The two after it are what a reader asks next — what the risks
 * are, and what the supplier actually is — and they follow the panel's own
 * order, so the pills read as a continuation of the surface they were opened
 * from rather than as a fresh menu.
 *
 * The fourth slot rotates like everywhere else, which keeps the surface moving
 * day to day and gives the next good question somewhere to go without a
 * decision about which of the three to drop.
 */
function buildSupplierSuggestions(rng: () => number, entityName: string): WelcomeSuggestion[] {
  // Every prompt says "supplier", not just the first. Each one is sent on its
  // own and the model never sees the chip's label, so a prompt carrying only the
  // company's name reads as a question about the company in general — and the
  // answer would come from the company profile tools rather than from
  // `get_supplier_risk_assessment`, which is the tool that holds the bands this
  // reader is looking at.
  const fixed: WelcomeSuggestion[] = [
    chip(
      `Executive summary`,
      `Give me an executive summary of the supplier risk assessment for ${entityName}, and the key take-aways for us as a buyer.`,
    ),
    chip(
      `Top risks`,
      `What are the top risks for ${entityName} as a supplier? Cover both the negative impacts it causes and the sustainability risks it is exposed to.`,
    ),
    chip(
      `Supplier model`,
      `What is ${entityName}'s business model as a supplier? List its products and services, and the locations it operates in.`,
    ),
  ];
  const wildcards: WelcomeSuggestion[] = [
    chip(
      `What should we do?`,
      `As a buyer, what should we do about the risks identified for our supplier ${entityName}?`,
    ),
    chip(
      `Physical climate exposure`,
      `How exposed are ${entityName}'s locations to physical climate hazards, and which hazard matters most?`,
    ),
    chip(
      `Why this risk level?`,
      `Why does ${entityName} come out at its overall supplier risk level, and which topics drive it?`,
    ),
    chip(
      `Compare with other suppliers`,
      `How does ${entityName} compare with our other suppliers on sustainability risk?`,
    ),
  ];

  return [...fixed, pick(rng, wildcards)];
}

/**
 * Standalone /chat (no entity binding) — the "explore the platform"
 * welcome. Rotates daily so a user returning the next day doesn't see
 * the same four pills. We keep the trailing "Screen tech companies…"
 * style pill at the end so PLT-4605's long-pill row treatment still
 * applies.
 */
function buildExploreSuggestions(rng: () => number): WelcomeSuggestion[] {
  // Lead with a DMA "why is this material" / net-impact chip — these are the
  // platform's core lenses, so the first thing a new user sees points at them
  // (Janne's activation steer, PLT-5350). SDG / SFDR / EU-Taxonomy chips are
  // intentionally NOT in the standalone explore set; they still surface in the
  // lens-bound pools when the user is actually on that lens.
  const leadPool: WelcomeSuggestion[] = [
    chip(`Nordea DMA`, `Show and summarize the DMA results for Nordea.`),
    chip(`Apple net impact`, `Show and summarize the net impact of Apple.`),
    chip(`Why is climate material for Shell?`, `Why is climate change a material matter for Shell?`),
    chip(`Nestlé's most material matters`, `What are the most material sustainability matters for Nestlé, and why?`),
  ];
  const shortPool: WelcomeSuggestion[] = [
    chip(
      `Tesla environmental risks`,
      `Which environmental risks should Tesla focus on mitigating over the next 5 years?`,
    ),
    chip(`Shell vs. BP`, `Compare the net impact and material matters of Shell and BP.`),
    chip(`Saab climate exposure`, `How exposed is Saab to climate-related risks?`),
    chip(`Nestlé social impact`, `Summarize Nestlé's social impact and main controversies.`),
    chip(`H&M value chain`, `Where in H&M's value chain do the biggest sustainability impacts arise?`),
  ];
  const longPool: WelcomeSuggestion[] = [
    chip(
      `Screen tech companies by sustainability`,
      `Show and summarize the sustainability performance of tech companies.`,
    ),
    chip(
      `Most material matters for European utilities`,
      `Which sustainability matters are most material for European utilities?`,
    ),
    chip(`Compare oil majors on net impact`, `Compare the net impact of the major oil and gas companies.`),
  ];

  const lead = pick(rng, leadPool);
  const shorts = pickDistinct(rng, shortPool, 2);
  const longs = [pick(rng, longPool)];
  return [lead, ...shorts, ...longs];
}

// ---------------------------------------------------------------------------
// Top-level entry point.
// ---------------------------------------------------------------------------

/**
 * Build the welcome-screen suggestion chips for the given context
 * snapshot. Pure function — callers pass in everything we need; no
 * module-level state.
 *
 * Returns a list of `WelcomeSuggestion`s ready to render. The caller
 * decides how to lay them out (PLT-4605's "long pill on its own row"
 * for the standalone case lives in the welcome component, since it's a
 * pure presentation concern).
 */
export function buildWelcomeSuggestions(input: WelcomeSuggestionsInput): WelcomeSuggestion[] {
  const now = input.now ?? Date.now();
  const seedSource = [
    input.entityType ?? '',
    input.entityName ?? '',
    input.lensLabel ?? '',
    input.detailLabel ?? '',
    String(dayBucket(now)),
  ].join('|');
  const rng = mulberry32(hashString(seedSource));
  const layout = input.layout ?? 'full-page';

  // No entity at all → the "explore the platform" set. Used on
  // standalone /chat loads with no page context.
  if (!input.entityName || input.entityName.length === 0 || input.entityType === null) {
    return applyLayoutCap(buildExploreSuggestions(rng), layout);
  }

  const entityName = input.entityName;
  const chips = (() => {
    switch (input.entityType) {
      case 'group':
        return buildGroupSuggestions(rng, entityName);
      case 'portfolio':
        return buildPortfolioSuggestions(rng, entityName);
      case 'supplier':
        return buildSupplierSuggestions(rng, entityName);
      case 'company':
      default:
        return buildCompanySuggestions(rng, entityName, input.lensLabel, input.detailLabel);
    }
  })();
  return applyLayoutCap(chips, layout);
}

// ---------------------------------------------------------------------------
// Static fallback — exported for the assistant-ui `Thread` config in
// `upright-chat.tsx`, which expects a plain array (not a function) on its
// `welcome.suggestions` prop. The custom `UprightChatWelcome` component
// overrides this anyway, so the array is only ever rendered as a last-
// resort fallback when our custom welcome isn't mounted. Generated once
// at module-load time with a stable explore-seed so the fallback isn't
// empty.
// ---------------------------------------------------------------------------
export const WELCOME_SUGGESTIONS_STATIC: WelcomeSuggestion[] = buildWelcomeSuggestions({
  entityName: null,
  entityType: null,
  lensLabel: null,
  detailLabel: null,
  // Pin the fallback to a fixed bucket so its content doesn't depend on
  // module load time — keeps it stable across deploys.
  now: 0,
});
